import { copyEditorSelection } from '../editor/clipboard'
import './cell-content-editor.css'

import { useContext, useEffect, useLayoutEffect, useRef } from 'react'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import type { TableCellDraft } from '../hooks/useTableCellDrafts'
import { verificationNodeView } from '../editor/verification-node-view'
import { referenceNodeView } from '../editor/reference-node-view'
import { ActiveTableReferenceContext, TableReferenceContext } from './TableReferenceContext'

/** The workspace owns immutable editor state; this view lives only while its row is mounted. */
export default function CellContentEditor(props: {
  draft: TableCellDraft
  onDraftChange: (draft: TableCellDraft) => void
  onSave: () => void
  onCancel: () => void
  label: string
  matterId?: string
  tableId?: string
  saving: boolean
  autoFocus: boolean
}) {
  const host = useRef<HTMLDivElement>(null)
  const view = useRef<EditorView | null>(null)
  const restoreFocus = useRef(false)
  const cancelled = useRef(false)
  const outsideInteraction = useRef(false)
  const openReference = useContext(TableReferenceContext)
  const activeReferenceId = useContext(ActiveTableReferenceContext)
  const latest = useRef({ ...props, openReference, activeReferenceId })
  useLayoutEffect(() => {
    latest.current = { ...props, openReference, activeReferenceId }
  })
  useEffect(() => {
    const saveOutside = (event: PointerEvent) => {
      const cell = host.current?.closest('td') ?? host.current
      if (event.target instanceof Node && cell && !cell.contains(event.target)) {
        outsideInteraction.current = true
        restoreFocus.current = false
        if (!cancelled.current) latest.current.onSave()
      }
    }
    document.addEventListener('pointerdown', saveOutside, true)
    return () => document.removeEventListener('pointerdown', saveOutside, true)
  }, [])
  useLayoutEffect(() => {
    const current = latest.current
    // Keep the original numbers while editing, including after unmount and undo.
    const numbers = new Map(current.draft.references.map((reference, index) => [reference.id, index + 1]))
    const references = new Map(current.draft.references.map((reference) => [reference.id, reference]))
    const editor = new EditorView(host.current!, {
      state: current.draft.editorState,
      attributes: {
        role: 'textbox',
        'aria-label': `Value for ${current.label}`,
        'aria-multiline': 'true',
        class: 'table-cell-editor min-h-8 w-full whitespace-pre-wrap break-words text-sm outline-hidden',
      },
      editable: () => !latest.current.saving,
      decorations: (state) => {
        const decorations: Decoration[] = []
        if (latest.current.activeReferenceId) {
          state.doc.descendants((node, position) => {
            if (node.type.name === 'reference' && node.attrs.id === latest.current.activeReferenceId) {
              decorations.push(Decoration.node(position, position + node.nodeSize, { 'data-active': 'true', 'aria-current': 'true' }))
            }
          })
        }
        return DecorationSet.create(state.doc, decorations)
      },
      dispatchTransaction: (transaction) => {
        // Freeze the submitted document until save resolves, so closing on success cannot discard newer edits.
        if (latest.current.saving && transaction.docChanged) return
        const state = editor.state.apply(transaction)
        editor.updateState(state)
        latest.current.onDraftChange({ ...latest.current.draft, editorState: state })
      },
      nodeViews: {
        verification: (_node, editorView, getPos) => verificationNodeView(editorView, getPos),
        reference: (node, editorView, getPos) =>
          referenceNodeView(
            node.attrs.id,
            numbers.get(node.attrs.id)!,
            references.get(node.attrs.id),
            editorView,
            getPos,
            current.matterId,
            (reference) => latest.current.openReference?.(reference) ?? false
          ),
      },
      handleKeyDown: (editorView, event) => {
        if (latest.current.saving) return true
        if (editorView.composing || event.isComposing) return false
        if (event.key === 'Escape') {
          cancelled.current = true
          latest.current.onCancel()
          return true
        }
        if (event.key !== 'Enter') return false
        if (event.shiftKey) editorView.dispatch(editorView.state.tr.insertText('\n').scrollIntoView())
        else latest.current.onSave()
        return true
      },
      handleDOMEvents: {
        copy: (editorView, event) => copyEditorSelection(editorView, event, latest.current.draft.references, latest.current.matterId, latest.current.tableId),
        cut: (editorView, event) => copyEditorSelection(editorView, event, latest.current.draft.references, latest.current.matterId, latest.current.tableId),
      },
      // Clipboard HTML cannot introduce references or formatting into an answer.
      handlePaste: (editorView, event) => {
        if (!event.clipboardData || latest.current.saving) return true
        const text = event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n')
        editorView.dispatch(editorView.state.tr.insertText(text).scrollIntoView())
        return true
      },
    })
    view.current = editor
    if (current.autoFocus) editor.focus()
    return () => {
      view.current = null
      editor.destroy()
    }
  }, [])
  useLayoutEffect(() => {
    const editor = view.current
    if (!editor) return
    if (editor.state !== props.draft.editorState) editor.updateState(props.draft.editorState)
    if (props.saving && editor.hasFocus() && !outsideInteraction.current) restoreFocus.current = true
    editor.setProps({ editable: () => !props.saving })
    if (!props.saving && restoreFocus.current) {
      if (document.activeElement === document.body || editor.dom.contains(document.activeElement)) editor.focus()
      restoreFocus.current = false
    }
    editor.dom.setAttribute('aria-disabled', String(props.saving))
  }, [props.draft.editorState, props.saving, activeReferenceId])
  return (
    <div
      ref={host}
      onFocus={() => {
        outsideInteraction.current = false
      }}
      onBlur={(event) => {
        const cell = host.current?.closest('td') ?? host.current
        if (!cancelled.current && event.relatedTarget instanceof Node && !cell?.contains(event.relatedTarget)) latest.current.onSave()
      }}
    />
  )
}
